import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import type { UserOrGroup } from 'src/schemas/global';

import { useInsertAcceptance, useUpdateAcceptance } from '@/hooks/mutations';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useGetAcceptanceById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';
import {
  type CreatedEntityDestination,
  saveFilesForOptionalCreatedEntity,
} from '@/utils/createWithFiles';
import { acceptanceDetailUrl } from '@/utils/urls';

import AcceptanceForm from '../forms/AcceptanceForm';
import type { AcceptanceFormDataFields } from '../forms/acceptanceSchema';
import { defaultValues } from '../forms/acceptanceSchema';

type Props = {
  Id?: string;
  ParentId?: string;
};

const getUserAndGroup = (field: null | undefined | UserOrGroup) => {
  let user: null | string = null;
  let userGroup: null | string = null;
  if (field) {
    switch (field.type) {
      case 'user':
        user = field.value;
        break;
      case 'userGroup':
        userGroup = field.value;
        break;
      default:
        console.error(`Unsupported type ${field.type}`);
    }
  }

  return { user, userGroup };
};

const getUserOrGroup = (
  user?: null | string,
  userGroup?: null | string
): null | UserOrGroup => {
  if (user) {
    return {
      type: 'user',
      value: user,
    };
  }
  if (userGroup) {
    return {
      type: 'userGroup',
      value: userGroup,
    };
  }

  return null;
};

const Tab: FC<Props> = ({ Id, ParentId }) => {
  const { updateFiles } = useFileUpdate();
  const navigate = useNavigate();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const formPrefillEnabled = useIsFormPrefillEnabled();

  const onDismiss = () => {
    navigate(-1);
  };

  const { data, loading, error } = useGetAcceptanceById({
    queryArgs: { acceptanceId: Id ?? '' },
    shouldSkip: !Id,
  });
  if (error) {
    throw error;
  }

  const acceptance = data?.acceptance[0];

  const {
    hasPermission: canUpdatedAcceptance,
    loading: isLoadingUpdatedAcceptance,
  } = useHasPermissionQuery('update:acceptance', acceptance);

  const canModify = canUpdatedAcceptance || !acceptance;

  const values = {
    ...defaultValues,
    ...acceptance,
    files: acceptance?.files.map((rf) => rf.file),
  };

  const acceptanceFormFields: AcceptanceFormDataFields | undefined = useMemo<
    AcceptanceFormDataFields | undefined
  >(() => {
    if (!acceptance) {
      return undefined;
    }
    const approvedBy = getUserOrGroup(
      acceptance.ApprovedByUser,
      acceptance.ApprovedByUserGroup
    );
    const requestedBy = getUserOrGroup(
      acceptance.RequestedByUser,
      acceptance.RequestedByUserGroup
    );

    const fields: AcceptanceFormDataFields = {
      ...acceptance,
      files: acceptance?.files.map((rf) => rf.file),
      approvedBy,
      requestedBy,
    };

    return fields;
  }, [acceptance]);

  const { insertAcceptance } = useInsertAcceptance();
  const { updateAcceptance } = useUpdateAcceptance();

  const onSave = async (data: AcceptanceFormDataFields) => {
    const { files, approvedBy, requestedBy, ...rest } = data;
    const { user: approvedByUser, userGroup: approvedByUserGroup } =
      getUserAndGroup(approvedBy);
    const { user: requestedByUser, userGroup: requestedByUserGroup } =
      getUserAndGroup(requestedBy);
    // Resolve to a local rather than reassigning the `Id` prop: existing id on
    // edit, the freshly-created id on create.
    let savedId: string | undefined = Id;
    let createdEntity: CreatedEntityDestination | undefined;
    if (acceptance) {
      const result = await updateAcceptance({
        ...rest,
        OriginalTimestamp: acceptance.ModifiedAtTimestamp,
        Id: acceptance.Id,
        ApprovedByUser: approvedByUser,
        ApprovedByUserGroup: approvedByUserGroup,
        RequestedByUser: requestedByUser,
        RequestedByUserGroup: requestedByUserGroup,
        CustomAttributeData: rest.CustomAttributeData || undefined,
      });
      if (result.updateChildAcceptance?.affected_rows !== 1) {
        throw new Error(
          'Records not updated. Record may have been updated by another user'
        );
      }
    } else {
      if (!ParentId) {
        throw new Error('Cannot insert acceptance without a parent');
      }
      const result = await insertAcceptance({
        ...rest,
        ParentId,
        ApprovedByUser: approvedByUser,
        ApprovedByUserGroup: approvedByUserGroup,
        RequestedByUser: requestedByUser,
        RequestedByUserGroup: requestedByUserGroup,
        CustomAttributeData: rest.CustomAttributeData || undefined,
      });
      savedId = result.insertChildAcceptance?.Id;
      if (savedId) {
        createdEntity = {
          id: savedId,
          destination: acceptanceDetailUrl(savedId),
        };
      }
    }
    if (!savedId) {
      throw new Error('Id must be set');
    }
    const persistedId = savedId;
    const saveFiles = () =>
      updateFiles({
        parentType: Parent_Type_Enum.Acceptance,
        parentId: persistedId,
        originalFiles: values?.files,
        selectedFiles: files,
      });
    await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });

    return { savedIds: [savedId], createdEntity };
  };

  if (loading || isLoadingUpdatedAcceptance) {
    return null;
  }

  return (
    <AcceptanceForm
      onSave={onSave}
      readOnly={!canModify}
      values={acceptanceFormFields}
      onDismiss={onDismiss}
      approvalConfig={{ object: acceptance }}
      publishToolbarState={objectToolbarEnabled}
      publishActiveForm={formPrefillEnabled}
      activeFormRecordId={Id ?? null}
    />
  );
};

export default Tab;
